import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Download, Mail, Linkedin } from "lucide-react";
import { designer } from "../data/portfolio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", subject: "", message: "" };

export const ContactSection = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name";
    if (!form.email.trim()) e.email = "An email helps me reply";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "That email looks off";
    if (!form.message.trim() || form.message.trim().length < 8)
      e.message = "A sentence or two would help";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onChange = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || "New enquiry from portfolio",
        message: form.message.trim(),
      });
      toast.success(data.message || "Your note is on its way.", {
        description: "I'll reply personally within a few days.",
      });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something didn't send. Please try again in a moment.";
      toast.error("Message not sent", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-pad relative overflow-hidden"
      data-testid="contact-section"
    >
      

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {/* Left */}
          <div className="col-span-12 md:col-span-5">
            <div className="eyebrow mb-6">— Say hello</div>
            <h2 className="font-display font-light leading-[0.92] tracking-tighter text-4xl md:text-5xl lg:text-6xl text-[#0E1A2B] mb-8">
              Let's make something <span className="text-[#E5326A]">quietly&nbsp;good</span>.
            </h2>
            <p className="text-[#6E7583] text-lg leading-relaxed mb-8 max-w-md">
              I'm booking design partnerships for Spring 2026. Product work, art direction,
              or a slow-cooked rebrand, I'd love to hear about it.
            </p>

            <div className="space-y-6">
              <div className="flex flex-col gap-3">
                <a
                  href={`mailto:${designer.email}`}
                  className="group inline-flex items-center gap-3 font-display text-xl md:text-2xl text-[#0E1A2B] link-underline"
                  data-testid="contact-email-link"
                >
                  <span className="w-9 h-9 rounded-full border border-[#E4E6EC] flex items-center justify-center group-hover:bg-[#0E1A2B] group-hover:border-[#0E1A2B] group-hover:text-[#FFFFFF] transition-colors duration-500">
                    <Mail size={15} />
                  </span>
                  {designer.email}
                </a>
                {designer.socials
                  .filter((s) => s.label === "LinkedIn")
                  .map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      className="group inline-flex items-center gap-3 font-display text-xl md:text-2xl text-[#0E1A2B] link-underline"
                      data-testid={`contact-social-${s.label.toLowerCase()}`}
                    >
                      <span className="w-9 h-9 rounded-full border border-[#E4E6EC] flex items-center justify-center group-hover:bg-[#0E1A2B] group-hover:border-[#0E1A2B] group-hover:text-[#FFFFFF] transition-colors duration-500">
                        <Linkedin size={15} />
                      </span>
                      {s.label}
                    </a>
                  ))}
              </div>
              <div>
                <div className="eyebrow mb-3">Résumé</div>
                <a
                  href={designer.resume}
                  download="Shakti-Rai-Lead-Designer.pdf"
                  className="group inline-flex items-center gap-2.5 pl-2 pr-5 py-2 rounded-full border border-[#0E1A2B] text-[#0E1A2B] text-sm tracking-[0.22em] uppercase hover:bg-[#0E1A2B] hover:text-[#FFFFFF] transition-colors duration-500"
                  data-testid="contact-resume-download"
                >
                  <span className="w-8 h-8 rounded-full bg-[#0E1A2B]/8 flex items-center justify-center group-hover:bg-[#FFFFFF]/15 transition-colors duration-500">
                    <Download size={14} />
                  </span>
                  Download PDF
                </a>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="col-span-12 md:col-span-7 md:pl-6 lg:pl-16">
            <motion.form
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="bg-white/60 backdrop-blur-sm border border-[#E4E6EC]/70 rounded-[2.2rem] p-8 md:p-12"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">Your name</span>
                  <input
                    type="text"
                    className="tactile-input"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Ada Lovelace"
                    data-testid="contact-input-name"
                  />
                  {errors.name && (
                    <span className="text-xs text-[#E5326A] mt-1" data-testid="contact-error-name">
                      {errors.name}
                    </span>
                  )}
                </label>
                <label className="flex flex-col gap-1">
                  <span className="eyebrow">Email</span>
                  <input
                    type="email"
                    className="tactile-input"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="ada@studio.com"
                    data-testid="contact-input-email"
                  />
                  {errors.email && (
                    <span className="text-xs text-[#E5326A] mt-1" data-testid="contact-error-email">
                      {errors.email}
                    </span>
                  )}
                </label>
              </div>

              <label className="flex flex-col gap-1 mt-8">
                <span className="eyebrow">Subject <span className="lowercase text-[#6E7583]/70">— optional</span></span>
                <input
                  type="text"
                  className="tactile-input"
                  value={form.subject}
                  onChange={onChange("subject")}
                  placeholder="A quiet product rebrand"
                  data-testid="contact-input-subject"
                />
              </label>

              <label className="flex flex-col gap-1 mt-8">
                <span className="eyebrow">Your message</span>
                <textarea
                  className="tactile-input resize-none min-h-[140px]"
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell me what you're building, a paragraph is plenty."
                  data-testid="contact-input-message"
                />
                {errors.message && (
                  <span className="text-xs text-[#E5326A] mt-1" data-testid="contact-error-message">
                    {errors.message}
                  </span>
                )}
              </label>

              <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <p className="text-xs text-[#6E7583] max-w-xs">
                  I read every message myself. Usually reply within 3 working days.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#E5326A] text-[#FFFFFF] text-sm tracking-[0.22em] uppercase hover:bg-[#0E1A2B] transition-colors duration-500 disabled:opacity-70"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowRight
                        size={16}
                        className="transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
